import {useI18n} from 'i18n/I18nContext'
import React, {useRef, useState, useEffect, useMemo} from 'react'

/** Placeholder tokens stored in certificate templates (must match server / defaults). */
const CERT_VAR_TOKENS = [
  'Име',
  'Фамилия',
  'Имейл',
  'Дата',
  'certLocation',
] as const

const VAR_LABEL_KEYS: Record<(typeof CERT_VAR_TOKENS)[number], string> = {
  Име: 'orgCert.varBtnGivenName',
  Фамилия: 'orgCert.varBtnFamilyName',
  Имейл: 'orgCert.varBtnEmail',
  Дата: 'orgCert.varBtnDate',
  certLocation: 'orgCert.varBtnLocation',
}

type FieldEditorProps = {
  title: string
  onChange: (value: string) => void
  defaultValue?: string
}

export const FieldEditor: React.FC<FieldEditorProps> = ({
  title,
  onChange,
  defaultValue = '',
}) => {
  const {t} = useI18n()
  const variableButtons = useMemo(
    () =>
      CERT_VAR_TOKENS.map((token) => ({
        token,
        label: t(VAR_LABEL_KEYS[token]),
      })),
    [t],
  )
  const [value, setValue] = useState(defaultValue)
  const [focused, setFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    setValue(text)
    onChange(text)
  }

  const insertVariable = (variable: string) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newValue =
      value.substring(0, start) + `{{${variable}}}` + value.substring(end)

    setValue(newValue)
    onChange(newValue)

    requestAnimationFrame(() => {
      textarea.focus()
      const caretPos = start + `{{${variable}}}`.length
      textarea.setSelectionRange(caretPos, caretPos)
    })
  }

  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [value])

  const highlightVariables = (text: string) => {
    const parts = text.split(/({{.*?}})/g)
    return parts.map((part, i) =>
      part.match(/^{{.*}}$/) ? (
        <span
          key={i}
          style={{
            background: '#ffec99',
            borderRadius: 4,
            whiteSpace: 'pre-wrap',
          }}
        >
          {part}
        </span>
      ) : (
        <span key={i} style={{whiteSpace: 'pre-wrap'}}>
          {part}
        </span>
      ),
    )
  }

  return (
    <div className="single__input-field mb-15" style={{marginBottom: 15}}>
      <label className="input__field-text" htmlFor={title}>
        {title}
      </label>

      <div style={{position: 'relative'}} className="event__input mb-15">
        <textarea
          id={title}
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            position: 'relative',
            background: 'transparent',
            color: 'transparent',
            caretColor: 'black',
            resize: 'none',
            lineHeight: 1.5,
            overflow: 'hidden',
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: 15,
            left: 15,
            right: 15,
            bottom: 15,
            pointerEvents: 'none',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            fontSize: 'var(--bs-body-font-size)',
            color: '#000',
            lineHeight: 1.5,
          }}
        >
          {highlightVariables(value)}
        </div>
      </div>

      {focused && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginTop: 8,
          }}
        >
          {variableButtons.map(({token, label}) => (
            <button
              key={token}
              type="button"
              title={`{{${token}}}`}
              onMouseDown={(e) => {
                e.preventDefault()
                insertVariable(token)
              }}
              style={{
                background: '#eee',
                border: '1px solid #ccc',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
